import { isEqual } from 'lodash';
import {
  ClassificationAttributes,
  ClassificationService,
  StartInteractiveClassificationRequest,
  StartInteractiveClassificationResult,
  SubmitClassificationAnswersRequest,
  SubmitClassificationAnswersResult,
  ValidateClassificationRequest,
  ValidateClassificationResult,
} from './classificationService';

/**
 * Mock classification service. Doesn't make any back-end calls.
 */
export class MockClassificationService implements ClassificationService {
  private static readonly DEMO_REASONING =
    'Based on your product details (a plastic household storage container with lid designed for home organization), I recommend HTS code 3924.10.4000. This classification covers household articles made of plastic, including storage containers with similar characteristics to your product.';

  private static readonly DEMO_MISMATCH_VALIDATION_REQUEST: ValidateClassificationRequest =
    {
      htsNumber: '4202.92.0000',
      productDescription: 'Green Hiking backpack',
      manufacturerName: 'Shenzhen Baishitong E-Commerce, Ltd',
      countryOfOrigin: 'CN',
    };

  private static readonly DEMO_INTERACTIVE_CLASSIFICATION_ATTRIBUTES: ClassificationAttributes =
    {
      productDescription: 'Plastic container',
      manufacturerName: 'Shenzhen Baishitong E-Commerce, Ltd',
      countryOfOrigin: 'CN',
    };

  private static readonly DEMO_NOT_ENOUGH_INFORMATION_VALIDATION_REQUEST: ValidateClassificationRequest =
    {
      htsNumber: '3924.10.0000',
      ...this.DEMO_INTERACTIVE_CLASSIFICATION_ATTRIBUTES,
    };

  private static readonly DEMO_INTERACTIVE_CLASSIFICATION_ID =
    '5c14c6a7-bfa9-42d1-b1c2-0c9d4d276e5a';

  private static readonly DEMO_EXPECTED_ANSWER_IDS = [
    '2d432700-a4a9-435d-920f-934e42cab62f',
    'a34a9877-c4d7-425b-9faf-c5d1dada149c',
    '5ce7253a-5d03-4a19-bad0-86e1c7b19d4e',
  ];

  validateClassification(
    request: ValidateClassificationRequest,
  ): ValidateClassificationResult {
    if (
      isEqual(
        request,
        MockClassificationService.DEMO_MISMATCH_VALIDATION_REQUEST,
      )
    ) {
      return {
        status: 'ERROR',
        errorCode: 'HTS_MISMATCH',
        classification: {
          id: '',
          status: 'COMPLETE',
          result: {
            htsNumber: '4202.92.3120',
            reasoning: MockClassificationService.DEMO_REASONING,
          },
        },
      };
    } else if (
      isEqual(
        request,
        MockClassificationService.DEMO_NOT_ENOUGH_INFORMATION_VALIDATION_REQUEST,
      )
    ) {
      return {
        status: 'ERROR',
        errorCode: 'NOT_ENOUGH_INFORMATION',
      };
    } else {
      return {
        status: 'VALIDATED',
      };
    }
  }

  startInteractiveClassification(
    request: StartInteractiveClassificationRequest,
  ): StartInteractiveClassificationResult {
    if (
      isEqual(
        request,
        MockClassificationService.DEMO_INTERACTIVE_CLASSIFICATION_ATTRIBUTES,
      )
    ) {
      return {
        id: MockClassificationService.DEMO_INTERACTIVE_CLASSIFICATION_ID,
        status: 'IN_PROGRESS',
        questions: [
          {
            id: 'cbe481ed-a2e0-4e68-b56d-b3f3543d6e30',
            text: 'What is the main material used in your storage container?',
            type: 'SINGLE_SELECT',
            options: [
              {
                id: MockClassificationService.DEMO_EXPECTED_ANSWER_IDS[0],
                text: 'Plastic',
              },
              {
                id: '8185d437-0961-4194-b016-e5e2bbc46352',
                text: 'Metal',
              },
              {
                id: '7eceb030-8c7e-42ca-b56f-d1ffa8bde9b3',
                text: 'Wood',
              },
              {
                id: 'e21e4e47-414a-408b-94a7-34f87fd3277a',
                text: 'Mixed materials',
              },
            ],
          },
          {
            id: '5c8bba32-4658-44e3-b077-d6fed9239a2f',
            text: "What is the product's intended use?",
            type: 'SINGLE_SELECT',
            options: [
              {
                id: MockClassificationService.DEMO_EXPECTED_ANSWER_IDS[1],
                text: 'Household storage',
              },
              {
                id: '755c49d9-128a-4017-b6ba-5efae5ef9f77',
                text: 'Commercial/Industrial storage',
              },
              {
                id: '824854fc-8ad8-49f5-b6cf-ed2d5c48715b',
                text: 'Food storage/Kitchen usage',
              },
            ],
          },
          {
            id: '2147c069-0a5a-438b-bc74-ba43ac668e0f',
            text: 'Is the item:',
            type: 'SINGLE_SELECT',
            options: [
              {
                id: MockClassificationService.DEMO_EXPECTED_ANSWER_IDS[2],
                text: 'With lid/cover',
              },
              {
                id: '0fdfd30b-3843-46b3-9904-327511a56c0e',
                text: 'Without lid/cover',
              },
              {
                id: 'a3dfe438-80c9-4de8-83fd-016945a6b1b8',
                text: 'Multi-piece set',
              },
              {
                id: 'bafbcae9-f330-40ff-928b-066eba3ed216',
                text: 'Nesting/Stackable only',
              },
            ],
          },
        ],
      };
    } else {
      throw new Error(
        `Could not start classification request for product with description: ${request.productDescription}, manufacturer: ${request.manufacturerName}, countryOfOrigin: ${request.countryOfOrigin}`,
      );
    }
  }

  submitClassificationAnswers(
    request: SubmitClassificationAnswersRequest,
  ): SubmitClassificationAnswersResult {
    const isDemoClassification =
      request.classificationId ===
      MockClassificationService.DEMO_INTERACTIVE_CLASSIFICATION_ID;

    const isExpectedAnswers = request.answers.every((answer) =>
      MockClassificationService.DEMO_EXPECTED_ANSWER_IDS.includes(
        answer.answerId,
      ),
    );

    if (isDemoClassification && isExpectedAnswers) {
      return {
        id: MockClassificationService.DEMO_INTERACTIVE_CLASSIFICATION_ID,
        status: 'COMPLETE',
        result: {
          htsNumber: '3924.10.4000',
          reasoning: MockClassificationService.DEMO_REASONING,
        },
      };
    } else {
      throw new Error(
        `Could not submit classification answers for classification: ${request.classificationId}`,
      );
    }
  }
}
